// Browser-side Meta pixel helpers. The pixel is only initialised after
// advertising consent (see MarketingTags); these helpers wait for that and
// send each event id at most once per page load.

export const META_PIXEL_ID = "2031158990296368";
export const META_PIXEL_READY_EVENT = "guestgallery:meta-pixel-ready";

interface MetaPixelWindow {
  fbq?: (...args: any[]) => void;
  __guestGalleryMetaPixelReady?: boolean;
}

const sentEventKeys = new Set<string>();
let hasTrackedPageView = false;

function getMetaWindow(): MetaPixelWindow {
  return window as unknown as MetaPixelWindow;
}

/** Runs `callback` once the pixel has been initialised. Returns a cleanup. */
export function whenMetaPixelReady(callback: () => void): () => void {
  const w = getMetaWindow();
  if (w.__guestGalleryMetaPixelReady && typeof w.fbq === "function") {
    callback();
    return () => {};
  }
  const listener = (): void => callback();
  window.addEventListener(META_PIXEL_READY_EVENT, listener, { once: true });
  return () => window.removeEventListener(META_PIXEL_READY_EVENT, listener);
}

/** Sends a browser event with an event id shared with the server event. */
export function trackMetaPixelEvent(
  eventName: string,
  data: Record<string, unknown>,
  eventId: string,
): void {
  const fbq = getMetaWindow().fbq;
  const key = `${eventName}:${eventId}`;
  if (typeof fbq !== "function" || !eventId || sentEventKeys.has(key)) return;
  sentEventKeys.add(key);
  fbq("track", eventName, data, { eventID: eventId });
}

/**
 * One PageView per document load, like the stock pixel snippet. Layout
 * re-renders (router refresh, server actions) bring new event ids; the server
 * skips those renders too (PageViewTracker), so only the first id is used.
 */
export function trackMetaPageViewOnce(eventId: string): void {
  if (hasTrackedPageView || typeof getMetaWindow().fbq !== "function") return;
  hasTrackedPageView = true;
  trackMetaPixelEvent("PageView", {}, eventId);
}
